/*
 * Your Hall  ·  My Cast tab  ·  assets/hoh-hall-cast.js
 *
 * Renders the dramatis personae of the user's Hall: every saved hero
 * grouped under role tags, filterable by role chip and location text,
 * orderable via drag and drop, with a sticky Run Cast button that
 * hands the visible filtered cast to the existing Company tool.
 *
 * Public API:
 *   hohHallRenderCast(host)   render into the given DOM container
 *
 * Reads:
 *   window.hohHallGet()                hall data layer snapshot
 *   window.hohHallTagHero / setLocation / removeHero / reorder
 *   window.hohGetAllCharacters()       full pool, to look up details
 *   window.hohSmoothGridUpdate         FLIP grid animator
 *   window.hohOpenDetail               open detail modal
 *   window.hohOpenCompanyTool          launch Run Cast
 *
 * Writes via the data layer only — no direct localStorage here so the
 * single source of truth stays in hoh-hall-data.js.
 */
(function(){
  'use strict';

  function esc(s){
    if(s == null) return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  /* Per-tab filter state. Lives in module scope so it survives
     re-renders inside the same session. */
  /* R87: Default sort changed from 'order' (manual drag) to 'mine'
     (most hearted by you). Drag-to-reorder removed entirely — the
     manual order option is gone from the dropdown and the drag wiring
     is short-circuited below. The "mine" sort surfaces the heroes the
     user has put the most love into, which is what they actually
     reach for. */
  var FILTER = { search: '', role: '', location: '', sort: 'mine' };

  /* The full role preset list, exported by hoh-hall-data.js. */
  function roles(){ return (window.HOH_HALL_ROLE_PRESETS || ['ally','rival','boss','patron','quest_giver','background']).slice(); }
  function roleLabel(k){
    var L = window.HOH_HALL_ROLE_LABELS || {};
    return L[k] || (k.charAt(0).toUpperCase() + k.slice(1).replace('_',' '));
  }

  function rarityTier(c){
    try {
      if(typeof window.hohGetRarity === 'function') return window.hohGetRarity(c.hearts || 0).tier;
    } catch(e){}
    return 'common';
  }

  /* Resolve a saved hero entry into a fully decorated character row by
     merging the hall-side fields (roles, location, addedAt) with the
     pool-side fields (name, race, class, image, hearts). */
  function resolveSavedHeroes(){
    var hall = (typeof window.hohHallGet === 'function') ? window.hohHallGet() : { heroes: [] };
    var pool = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
    var byId = {};
    for(var i=0;i<pool.length;i++){ if(pool[i] && pool[i].id) byId[pool[i].id] = pool[i]; }
    var heartMap = {};
    try {
      var raw = localStorage.getItem('hoh-hearts');
      if(raw) heartMap = JSON.parse(raw) || {};
    } catch(e){}
    var rows = [];
    for(var k=0;k<hall.heroes.length;k++){
      var h = hall.heroes[k];
      if(!h || !h.id) continue;
      var c = byId[h.id];
      /* If the pool hasn't loaded yet OR the character was deleted on
         the server, we still render a placeholder so the user sees
         their save count is correct. The card uses the first-letter
         fallback. */
      var merged = c ? Object.assign({}, c) : { id: h.id, name: '(loading...)' };
      merged._hall = h;
      merged._mine = heartMap[h.id] || 0;
      rows.push(merged);
    }
    return rows;
  }

  function applyFilters(rows){
    var q = (FILTER.search || '').trim().toLowerCase();
    var loc = (FILTER.location || '').trim().toLowerCase();
    var role = FILTER.role;
    var out = rows.filter(function(r){
      if(!r) return false;
      var hh = r._hall || {};
      if(role){
        if(!Array.isArray(hh.roles) || hh.roles.indexOf(role) === -1) return false;
      }
      if(loc){
        if(!hh.location || String(hh.location).toLowerCase().indexOf(loc) === -1) return false;
      }
      if(q){
        var hay = (
          (r.name || '') + ' ' + (r.race || '') + ' ' + (r.character_class || '') +
          ' ' + (r.campaign || '') + ' ' + (r.player_name || '') + ' ' + (r.story || '') +
          ' ' + (hh.location || '')
        ).toLowerCase();
        if(hay.indexOf(q) === -1) return false;
      }
      return true;
    });
    if(FILTER.sort === 'most'){
      out.sort(function(a,b){ return (b.hearts||0) - (a.hearts||0); });
    } else if(FILTER.sort === 'newest'){
      out.sort(function(a,b){
        var ta = a._hall && a._hall.addedAt ? a._hall.addedAt : 0;
        var tb = b._hall && b._hall.addedAt ? b._hall.addedAt : 0;
        return tb - ta;
      });
    } else {
      /* R87: Default sort = 'mine' (most hearted by you). The legacy
         'order' value falls through here too — drag-to-reorder is
         retired so we no longer honor orderIndex. */
      out.sort(function(a,b){ return (b._mine||0) - (a._mine||0); });
    }
    return out;
  }

  /* Card markup. Reuses .hoh-yh-card so the existing rarity-tier
     borders kick in. Adds drag handle, action overlay (tag, remove),
     role chips, location line, and a hidden inline editor that drops
     in below the body when the user taps Tag. */
  function cardHtml(c){
    var hh = c._hall || {};
    var name = esc(c.name || 'Unnamed');
    var meta = esc((c.race || '') + (c.character_class ? ' ' + c.character_class : '') + (c.level ? ' · Lvl ' + c.level : ''));
    var firstLetter = esc((c.name || '?').charAt(0));
    var img = c.image_thumb_url || c.image_url;
    /* R265: apply the saved reframe on My Favorites / cast cards too, so a
       reframed hero looks the same here as on every other grid. */
    var castPos = (typeof window.hohImgObjPos === 'function') ? window.hohImgObjPos(c.id, 'primary') : '50% 50%';
    /* R769: through the combined style helper so the saved zoom rides along
       with the crop, the way every other grid already paints it. */
    var castStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + castPos + ';');
    var media = img
      ? '<img src="' + esc(img) + '" alt="' + name + '" loading="lazy" decoding="async" crossorigin="anonymous" data-letter="' + firstLetter + '" style="' + castStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-yh-letter">' + firstLetter + '</div>';
    var tier = rarityTier(c);
    var rolesArr = Array.isArray(hh.roles) ? hh.roles : [];
    var rolesHtml = rolesArr.length
      ? '<span class="hoh-cast-card-roles">' + rolesArr.map(function(r){
          return '<span class="hoh-cast-card-role" data-role="' + esc(r) + '">' + esc(roleLabel(r)) + '</span>';
        }).join('') + '</span>'
      : '';
    var locHtml = hh.location ? '<span class="hoh-cast-card-loc">' + esc(hh.location) + '</span>' : '';
    /* Heart count + "Yours" chips were intentionally removed from
       the My Favorites cards by request — those values already show
       on Profile cards, and Favorites is about role/location/notes,
       not popularity. Keeping the chip wrapper empty so existing CSS
       selectors don't break. */
    /* Editor pills: every preset role plus location + remove. */
    var pillsHtml = roles().map(function(r){
      var on = rolesArr.indexOf(r) !== -1 ? ' hoh-cast-editor-pill-on' : '';
      return '<button type="button" class="hoh-cast-editor-pill' + on + '" data-role-pill="' + esc(r) + '" data-hero-id="' + esc(c.id) + '">' + esc(roleLabel(r)) + '</button>';
    }).join('');
    var locVal = esc(hh.location || '');
    /* R100: Notes field shares the same data store the detail-modal
       notes use (hh.notes via hohHallSetNotes). Anything written in
       either surface lands in the same key — no duplicate state. */
    var notesVal = esc(hh.notes || '');
    var notesLen = (hh.notes || '').length;
    /* R103: Read-only display of the typed note in the collapsed
       (gear closed) state so the user's note isn't hidden behind the
       editor. CSS hides this element when the card has the
       hoh-cast-card-edit-open class so the editable textarea takes
       over without double-rendering. */
    var notesReadonlyHtml = (hh.notes && String(hh.notes).trim().length)
      ? '<span class="hoh-cast-card-notes-readonly" data-hero-id="' + esc(c.id) + '">' + notesVal + '</span>'
      : '';
    var canReorder = (FILTER.sort === 'order') ? ' hoh-cast-can-reorder' : '';
    /* R62: editor (role pills + location input) is OPEN BY DEFAULT.
       Was hidden until the user tapped the gear; now every card
       boots with the role tags exposed so users can scan/edit at a
       glance. The gear toggles closed/reopen per-card; cards are
       independent (no auto-close-others). */
    return '<div class="hoh-yh-card hoh-cast-card hoh-cast-card-edit-open hoh-yh-rar-' + tier + canReorder + '" data-hero-id="' + esc(c.id) + '">' +
        '<button type="button" class="hoh-cast-card-handle" aria-label="Drag to reorder" tabindex="-1">&#x2630;</button>' +
        /* Gear (edit) lives top-right and is the entry point for the
           inline editor. Remove (X) lives top-left and is INTENTIONALLY
           hidden until the user opens the editor — this prevents
           accidental hero removal from the grid. CSS does the gating
           via .hoh-cast-card-edit-open on the card. */
        '<button type="button" class="hoh-cast-action-btn hoh-cast-action-edit" data-act="edit" aria-label="Tag and notes">&#9881;&#xFE0E;</button>' +
        '<button type="button" class="hoh-cast-action-btn hoh-cast-action-remove" data-act="remove" aria-label="Remove from hall">&#10005;</button>' +
        /* Media wrapper is a DIV (role=button) instead of a real
           <button>. A button element swallows native HTML5 drag start
           events on most browsers — meaning the user couldn't grab
           the portrait area to drag the whole card. With a div, the
           dragstart bubbles up to the card's draggable=true and
           reorder works from anywhere on the card. Keyboard
           activation is preserved via tabindex + keydown. */
        '<div role="button" tabindex="0" class="hoh-yh-media" data-cast-open="' + esc(c.id) + '" aria-label="Open ' + name + '" style="cursor:pointer;display:block;" onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();this.click();}">' + media + '</div>' +
        '<div class="hoh-yh-body">' +
          '<span class="hoh-yh-name">' + name + '</span>' +
          '<span class="hoh-yh-meta">' + meta + '</span>' +
          locHtml +
          rolesHtml +
          notesReadonlyHtml +
        '</div>' +
        '<div class="hoh-cast-card-editor" aria-hidden="true">' +
          '<div class="hoh-cast-editor-row" role="group" aria-label="Roles">' + pillsHtml + '</div>' +
          '<input type="text" class="hoh-cast-editor-loc" data-hero-id="' + esc(c.id) + '" placeholder="Location (e.g. Waterdeep)" maxlength="60" value="' + locVal + '">' +
          /* R100: Notes textarea + save bar. Auto-saves on blur via the
             change handler (same pattern as the location input above)
             and an explicit Save button gives the user a click target.
             Live char counter mirrors the detail-modal notes UX. */
          '<textarea class="hoh-cast-editor-notes" data-hero-id="' + esc(c.id) + '" placeholder="Notes — campaign role, last-seen scene, anything you want to remember." maxlength="280" rows="3">' + notesVal + '</textarea>' +
          '<div class="hoh-cast-editor-notes-bar">' +
            '<span class="hoh-cast-editor-notes-saved" data-hero-id="' + esc(c.id) + '" data-state="idle">Saved</span>' +
            '<span class="hoh-cast-editor-notes-count" data-hero-id="' + esc(c.id) + '">' + notesLen + ' / 280</span>' +
            '<button type="button" class="hoh-cast-editor-notes-save" data-act="save-notes" data-hero-id="' + esc(c.id) + '">Save</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  /* The role chip strip + location input. Acts as the cast filter
     bar. Independent of the existing race/class filter row below. */
  function filterStripHtml(){
    var chips = roles().map(function(r){
      var on = (FILTER.role === r) ? ' hoh-cast-chip-on' : '';
      return '<button type="button" class="hoh-cast-chip' + on + '" data-role="' + esc(r) + '">' +
        '<span class="hoh-cast-chip-dot"></span>' + esc(roleLabel(r)) +
      '</button>';
    }).join('');
    var allOn = (FILTER.role === '') ? ' hoh-cast-chip-on' : '';
    return '<div class="hoh-cast-filter-strip">' +
      '<div class="hoh-cast-chips">' +
        '<button type="button" class="hoh-cast-chip' + allOn + '" data-role="">All</button>' +
        chips +
      '</div>' +
      '<input type="text" class="hoh-cast-loc-input" id="hoh-cast-loc-input" placeholder="Filter by location" value="' + esc(FILTER.location) + '" maxlength="60">' +
    '</div>';
  }

  function filterRowHtml(){
    var s = FILTER;
    /* R87: 'order' option removed — drag-to-reorder is retired. 'mine'
       is now the default and what the user wanted called "most hearted
       by you". */
    var sortOpts = [
      ['mine',   'Most hearted by you'],
      ['most',   'Most hearts'],
      ['newest', 'Newest']
    ];
    var sortHtml = sortOpts.map(function(o){
      return '<option value="' + o[0] + '"' + (o[0] === s.sort ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
    }).join('');
    return '<div class="hoh-yh-filter-row">' +
      '<input type="text" id="hoh-cast-search" class="hoh-yh-search-input" placeholder="Search by name, race, class, location..." value="' + esc(s.search) + '" />' +
      '<select id="hoh-cast-sort" class="hoh-yh-filter-select">' + sortHtml + '</select>' +
    '</div>';
  }

  function emptyHtml(){
    return '<div class="hoh-cast-empty">' +
      '<strong>No favorites yet.</strong><br>' +
      'Heart or star any hero on the main hall and they land here. Tag them as Allies, Rivals, Bosses — whatever fits the role they play in your campaign.' +
      '<br><button type="button" class="hoh-cast-empty-cta" onclick="if(window.hohYhToggleClick)hohYhToggleClick();else if(window.hohSwitchHallTab)hohSwitchHallTab(\'all\');">Browse the Hall</button>' +
    '</div>';
  }

  function paint(host, opts){
    var rows = resolveSavedHeroes();
    var filtered = applyFilters(rows);

    /* Top-level layout: chip strip, filter row, grid.
       The Run Cast sticky bar was removed in v2.2 — running a
       company now happens inside Satchel > Company by picking
       a saved party. Keeping the run-from-pool action on the
       Cast tab created a duplicate path; the Satchel surface
       owns it cleanly. */
    if(!opts || !opts.gridOnly){
      host.innerHTML =
        filterStripHtml() +
        filterRowHtml() +
        '<div id="hoh-cast-grid" class="hoh-yh-grid hoh-cast-grid"></div>';
      wireFilterStrip(host);
      wireFilterRow(host);
    }

    var grid = document.getElementById('hoh-cast-grid');
    if(!grid) return;

    if(rows.length === 0){
      grid.innerHTML = emptyHtml();
      return;
    }
    if(filtered.length === 0){
      grid.innerHTML = '<div class="hoh-yh-empty">No heroes match those filters.</div>';
      return;
    }

    /* Use the FLIP grid animator if present so reorders glide. */
    if(typeof window.hohSmoothGridUpdate === 'function'){
      window.hohSmoothGridUpdate(grid, filtered, {
        key: function(c){ return c.id; },
        build: function(c){ return cardHtml(c); }
      });
    } else {
      grid.innerHTML = filtered.map(cardHtml).join('');
    }
    /* Attach delegated listeners ONCE per grid lifetime. The flag
       lives on the DOM element so a re-mount (host innerHTML reset)
       gets a fresh attachment. */
    if(!grid.__hohCastWired){
      grid.__hohCastWired = true;
      wireCardActions(grid);
      wireDragReorder(grid);
    }
    /* Refresh draggable attribute on cards; only enable when sort is
       'order' AND we\'re on a non-touch viewport. Touch drag-reorder
       conflicts with scroll on iOS and is fiddly on small screens —
       mobile users get the gear + X actions only. The CSS hides the
       handle to match. */
    var allCards = grid.querySelectorAll('.hoh-cast-card');
    /* R87: Drag-to-reorder removed entirely. All cards stay non-draggable
       on both mobile and desktop. The drag wiring below short-circuits
       so even legacy 'order' values from older saved state can't trigger
       a drag. */
    for(var i=0;i<allCards.length;i++){
      allCards[i].setAttribute('draggable', 'false');
    }
  }

  function wireFilterStrip(host){
    var strip = host.querySelector('.hoh-cast-filter-strip');
    if(!strip) return;
    strip.addEventListener('click', function(e){
      var chip = e.target.closest && e.target.closest('.hoh-cast-chip');
      if(!chip) return;
      var role = chip.getAttribute('data-role') || '';
      FILTER.role = role;
      paint(host);
    });
    var loc = strip.querySelector('#hoh-cast-loc-input');
    if(loc){
      var t;
      loc.addEventListener('input', function(){
        clearTimeout(t);
        t = setTimeout(function(){
          FILTER.location = loc.value;
          paint(host, { gridOnly: true });
          /* gridOnly skips the filter strip rerender so the user can
             keep typing without focus loss. The run bar count won't
             update in gridOnly mode, but that's a minor cosmetic
             issue we can fix in a follow-up by re-rendering only the
             count node. */
        }, 180);
      });
    }
  }

  function wireFilterRow(host){
    var row = host.querySelector('.hoh-yh-filter-row');
    if(!row) return;
    var search = row.querySelector('#hoh-cast-search');
    var sort   = row.querySelector('#hoh-cast-sort');
    if(search){
      var t;
      search.addEventListener('input', function(){
        clearTimeout(t);
        t = setTimeout(function(){
          FILTER.search = search.value;
          paint(host, { gridOnly: true });
        }, 160);
      });
    }
    if(sort){
      sort.addEventListener('change', function(){
        FILTER.sort = sort.value;
        paint(host);
      });
    }
  }

  function wireCardActions(grid){
    /* Click delegation: the grid handles edit/remove buttons, role
       pill toggles, location input blur, and image clicks. Avoids
       attaching one listener per card. */
    grid.addEventListener('click', function(e){
      var t = e.target;
      if(!t) return;
      /* Image opens detail. */
      var media = t.closest && t.closest('[data-cast-open]');
      if(media){
        e.preventDefault();
        var openId = media.getAttribute('data-cast-open');
        if(openId && typeof window.hohOpenDetail === 'function') window.hohOpenDetail(openId);
        return;
      }
      var card = t.closest && t.closest('.hoh-cast-card');
      if(!card) return;
      var heroId = card.getAttribute('data-hero-id');
      if(!heroId) return;
      /* R100: Save Notes button — explicit click path. The textarea
         also auto-saves on blur via the change handler below; this
         button gives users a clickable confirm + flushes immediately
         without waiting for blur (handy on mobile where blurring
         a textarea sometimes requires tapping somewhere else first). */
      var saveNotesBtn = t.closest && t.closest('.hoh-cast-editor-notes-save');
      if(saveNotesBtn){
        e.preventDefault();
        var noteHeroId = saveNotesBtn.getAttribute('data-hero-id');
        if(noteHeroId){
          var ta = card.querySelector('.hoh-cast-editor-notes[data-hero-id="' + noteHeroId + '"]');
          if(ta) saveNotesFromTextarea(ta);
        }
        return;
      }
      var actBtn = t.closest && t.closest('.hoh-cast-action-btn');
      if(actBtn){
        e.preventDefault();
        var act = actBtn.getAttribute('data-act');
        if(act === 'edit'){
          /* R62: cards are independent now — no auto-close-others.
             Editor is open by default for every card; gear toggles
             this specific card collapsed/reopened. */
          card.classList.toggle('hoh-cast-card-edit-open');
        } else if(act === 'remove'){
          /* Confirmation gate so a stray tap on the X doesn\'t lose
             a favorite irreversibly. Native confirm() works on both
             mobile + desktop and matches the user\'s OS conventions
             (iOS sheet, Android dialog, desktop browser confirm).
             The user\'s name is included in the prompt so they know
             which hero they\'re about to remove. */
          var heroName = '';
          try {
            var nameEl = card.querySelector('.hoh-yh-name');
            if(nameEl) heroName = (nameEl.textContent || '').trim();
          } catch(_){}
          var prompt = heroName
            ? 'Remove ' + heroName + ' from favorites?'
            : 'Remove this hero from favorites?';
          var ok = false;
          try { ok = window.confirm(prompt); } catch(_){ ok = true; }
          if(!ok) return;
          if(typeof window.hohHallRemoveHero === 'function'){
            window.hohHallRemoveHero(heroId);
            paint(grid.parentNode);
            if(typeof window.hohShowHallToast === 'function'){
              window.hohShowHallToast('Removed from favorites');
            }
          }
        }
        return;
      }
      var pill = t.closest && t.closest('.hoh-cast-editor-pill');
      if(pill){
        e.preventDefault();
        var role = pill.getAttribute('data-role-pill');
        var on = !pill.classList.contains('hoh-cast-editor-pill-on');
        if(typeof window.hohHallTagHero === 'function'){
          var ok = window.hohHallTagHero(heroId, role, on);
          if(ok){
            pill.classList.toggle('hoh-cast-editor-pill-on', on);
            /* Update the chip line on the card body without a full
               repaint to keep the editor open and animation smooth. */
            updateCardRolesInline(card, heroId);
          }
        }
        return;
      }
    });
    grid.addEventListener('change', function(e){
      var t = e.target;
      if(t && t.classList && t.classList.contains('hoh-cast-editor-loc')){
        var heroId = t.getAttribute('data-hero-id');
        if(heroId && typeof window.hohHallSetLocation === 'function'){
          window.hohHallSetLocation(heroId, t.value);
          var card = t.closest('.hoh-cast-card');
          if(card){
            var locEl = card.querySelector('.hoh-cast-card-loc');
            if(locEl){
              if(t.value){ locEl.textContent = t.value; locEl.style.display = ''; }
              else { locEl.style.display = 'none'; }
            } else if(t.value){
              /* No location element existed before; do a full repaint. */
              paint(grid.parentNode);
            }
          }
        }
      }
      /* R100: Notes auto-save on blur. Same pattern as location above —
         the textarea fires 'change' once focus leaves it AND the value
         actually changed, which is exactly the UX the user asked for
         ("save when they click off"). The explicit Save button below
         gives a clickable confirm path for users who'd rather press it. */
      if(t && t.classList && t.classList.contains('hoh-cast-editor-notes')){
        saveNotesFromTextarea(t);
      }
    });
    /* R100: Live char counter while typing — mirrors the detail-modal
       notes UX so users see the 280 cap as they approach it. The actual
       persistence still happens on blur (above) or Save click (below). */
    grid.addEventListener('input', function(e){
      var t = e.target;
      if(t && t.classList && t.classList.contains('hoh-cast-editor-notes')){
        var heroId = t.getAttribute('data-hero-id');
        if(!heroId) return;
        var card = t.closest('.hoh-cast-card');
        if(!card) return;
        var countEl = card.querySelector('.hoh-cast-editor-notes-count[data-hero-id="' + heroId + '"]');
        if(countEl) countEl.textContent = t.value.length + ' / 280';
        var savedEl = card.querySelector('.hoh-cast-editor-notes-saved[data-hero-id="' + heroId + '"]');
        if(savedEl){
          savedEl.textContent = 'Unsaved';
          savedEl.setAttribute('data-state', 'dirty');
        }
      }
    });
  }

  /* R100: Shared save path — used by both the blur auto-save and the
     explicit Save button click below. Persists via hohHallSetNotes
     (same key the detail modal writes), then flashes "Saved" feedback.
     R103: Also keeps the read-only collapsed-state notes element in
     sync so once the editor closes the note shows the latest value. */
  function saveNotesFromTextarea(ta){
    if(!ta) return;
    var heroId = ta.getAttribute('data-hero-id');
    if(!heroId || typeof window.hohHallSetNotes !== 'function') return;
    window.hohHallSetNotes(heroId, ta.value);
    var card = ta.closest('.hoh-cast-card');
    if(!card) return;
    var savedEl = card.querySelector('.hoh-cast-editor-notes-saved[data-hero-id="' + heroId + '"]');
    if(savedEl){
      savedEl.textContent = 'Saved';
      savedEl.setAttribute('data-state', 'saved');
      /* Brief flash, then settle to idle. Class swap triggers the
         CSS transition for the green tick fade. */
      setTimeout(function(){
        try { if(savedEl.getAttribute('data-state') === 'saved') savedEl.setAttribute('data-state', 'idle'); } catch(_){}
      }, 1400);
    }
    /* R103: Update or add the read-only notes element in the body so
       the closed-gear state reflects the new text immediately. */
    try {
      var body = card.querySelector('.hoh-yh-body');
      var ro = card.querySelector('.hoh-cast-card-notes-readonly[data-hero-id="' + heroId + '"]');
      var v = (ta.value || '').trim();
      if(v){
        if(!ro && body){
          ro = document.createElement('span');
          ro.className = 'hoh-cast-card-notes-readonly';
          ro.setAttribute('data-hero-id', heroId);
          body.appendChild(ro);
        }
        if(ro) ro.textContent = ta.value;
      } else if(ro && ro.parentNode){
        ro.parentNode.removeChild(ro);
      }
    } catch(_){}
  }

  function updateCardRolesInline(card, heroId){
    var roleArr = (typeof window.hohHallGetRoles === 'function') ? window.hohHallGetRoles(heroId) : [];
    var existing = card.querySelector('.hoh-cast-card-roles');
    var html = roleArr.length
      ? roleArr.map(function(r){
          return '<span class="hoh-cast-card-role" data-role="' + esc(r) + '">' + esc(roleLabel(r)) + '</span>';
        }).join('')
      : '';
    if(existing){
      if(html) existing.innerHTML = html;
      else existing.parentNode.removeChild(existing);
    } else if(html){
      var body = card.querySelector('.hoh-yh-body');
      if(body){
        var span = document.createElement('span');
        span.className = 'hoh-cast-card-roles';
        span.innerHTML = html;
        var loc = body.querySelector('.hoh-cast-card-loc');
        if(loc) body.insertBefore(span, loc.nextSibling);
        else body.appendChild(span);
      }
    }
  }

  /* Drag-to-reorder.

     Listeners live on the grid container, not per card, so we don't
     stack handlers when paint() re-runs. We only react when
     FILTER.sort === 'order' so other sorts produce a deterministic
     view that drag wouldn't override. The card-level draggable
     attribute is toggled inside paint() based on the current sort. */
  function wireDragReorder(grid){
    /* R87: Drag-to-reorder retired. The function is left as a no-op
       so any external caller still wired against it doesn't break,
       and so the FILTER.sort === 'order' guard inside is dead code. */
    return;
    /* eslint-disable no-unreachable */
    var draggingEl = null;
    var pressTimer = null;

    /* Desktop: HTML5 DnD via delegation. */
    grid.addEventListener('dragstart', function(e){
      if(FILTER.sort !== 'order') return;
      var card = e.target.closest && e.target.closest('.hoh-cast-card');
      if(!card) return;
      draggingEl = card;
      card.classList.add('hoh-cast-dragging');
      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', card.getAttribute('data-hero-id') || ''); } catch(_){}
    });
    grid.addEventListener('dragend', function(e){
      var card = e.target.closest && e.target.closest('.hoh-cast-card');
      if(card) card.classList.remove('hoh-cast-dragging');
      if(draggingEl){ commitOrder(grid); draggingEl = null; }
    });
    grid.addEventListener('dragover', function(e){
      if(!draggingEl) return;
      /* preventDefault must be called on EVERY dragover while a drag
         is in progress, otherwise the browser treats the drop target
         as invalid and the dragged card snaps back to its origin
         even after DOM reorder. The previous implementation only
         called preventDefault when the cursor was over another card,
         which meant dropping on empty grid space (the gap below the
         last row) cancelled the drop. */
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'move'; } catch(_){}
      var card = e.target.closest && e.target.closest('.hoh-cast-card');
      if(!card || card === draggingEl) return;
      var rect = card.getBoundingClientRect();
      var midX = rect.left + rect.width / 2;
      var midY = rect.top + rect.height / 2;
      var before = (e.clientY < midY) || (Math.abs(e.clientY - midY) < 4 && e.clientX < midX);
      if(before){
        if(card.previousElementSibling !== draggingEl) grid.insertBefore(draggingEl, card);
      } else {
        if(card.nextElementSibling !== draggingEl) grid.insertBefore(draggingEl, card.nextElementSibling);
      }
    });
    /* Explicit drop handler — preventDefault stops the browser from
       trying to navigate or open the dropped data, and signals that
       the drop was accepted (no snap-back). The actual commit fires
       on dragend below since that's where draggingEl is still
       available regardless of how the drag ended. */
    grid.addEventListener('drop', function(e){
      if(!draggingEl) return;
      e.preventDefault();
    });

    /* Mobile: long-press to grab, pointermove to slide, pointerup to
       release. Same delegated pattern. */
    grid.addEventListener('pointerdown', function(e){
      if(FILTER.sort !== 'order') return;
      if(e.pointerType !== 'touch') return;
      var card = e.target.closest && e.target.closest('.hoh-cast-card');
      if(!card) return;
      if(e.target.closest && (e.target.closest('.hoh-cast-card-actions') || e.target.closest('.hoh-cast-card-editor'))) return;
      pressTimer = setTimeout(function(){
        draggingEl = card;
        card.classList.add('hoh-cast-dragging');
        try { card.setPointerCapture && card.setPointerCapture(e.pointerId); } catch(_){}
      }, 350);
    });
    grid.addEventListener('pointermove', function(e){
      if(!draggingEl || e.pointerType !== 'touch') return;
      var el = document.elementFromPoint(e.clientX, e.clientY);
      var hit = el && el.closest && el.closest('.hoh-cast-card');
      if(hit && hit !== draggingEl){
        var rect = hit.getBoundingClientRect();
        var midY = rect.top + rect.height / 2;
        if(e.clientY < midY){
          if(hit.previousElementSibling !== draggingEl) grid.insertBefore(draggingEl, hit);
        } else {
          if(hit.nextElementSibling !== draggingEl) grid.insertBefore(draggingEl, hit.nextElementSibling);
        }
      }
    }, { passive: true });
    grid.addEventListener('pointerup', function(){
      clearTimeout(pressTimer);
      if(draggingEl){
        draggingEl.classList.remove('hoh-cast-dragging');
        commitOrder(grid);
        draggingEl = null;
      }
    });
    grid.addEventListener('pointercancel', function(){
      clearTimeout(pressTimer);
      if(draggingEl){
        draggingEl.classList.remove('hoh-cast-dragging');
        draggingEl = null;
      }
    });
  }

  function commitOrder(grid){
    if(typeof window.hohHallReorder !== 'function') return;
    var ids = [];
    var cards = grid.querySelectorAll('.hoh-cast-card');
    for(var i=0;i<cards.length;i++){
      var id = cards[i].getAttribute('data-hero-id');
      if(id) ids.push(id);
    }
    if(ids.length) window.hohHallReorder(ids);
  }

  /* ------------------------------------------------------------------ */
  /* Public entry point                                                 */
  /* ------------------------------------------------------------------ */

  window.hohHallRenderCast = function(host){
    if(!host) return;
    paint(host);
    /* Subscribe to data-layer changes so an external action (heart,
       save, remove from another part of the page) re-renders the
       grid in place. Cleared on next renderCast call via a single
       module-level handle. */
    if(window.__hohCastUnsub){ try { window.__hohCastUnsub(); } catch(_){} }
    if(typeof window.hohHallSubscribe === 'function'){
      window.__hohCastUnsub = window.hohHallSubscribe(function(){
        /* Only re-paint if our host is still on screen (user could
           have switched tabs since). */
        if(document.body.contains(host) && document.getElementById('hoh-cast-grid')){
          paint(host, { gridOnly: true });
        }
      });
    }
  };

  /* Re-render when the section signals characters have loaded so the
     cast cards pick up real names and portraits from the pool. */
  document.addEventListener('hoh:characters-loaded', function(){
    if(document.getElementById('hoh-cast-grid')){
      var host = document.getElementById('hoh-yh-tab-content');
      if(host) paint(host, { gridOnly: true });
    }
  });
})();
